'use client';

import type { ReactNode } from 'react';

type SettingsRowGroupGap = 'none' | 'md';

type SettingsRowGroupProps = {
  /**
   * Separation between children. Rows take `none` — their hairlines have to read
   * as one continuous list. `md` is for a panel that stacks blocks rather than
   * rows (a standing note, then the list), which needs the prototype's row gap
   * between them.
   */
  gap?: SettingsRowGroupGap;
  children: ReactNode;
  className?: string;
};

const GAPS: Record<SettingsRowGroupGap, string> = {
  none: '',
  md: ' gap-space-20',
};

/**
 * A run of `SettingsRow`s with no section title, sitting directly under the page
 * header — the prototype's General page opens with exactly this.
 *
 * Needed because `SettingsPage` separates its children by the prototype's
 * section gap; consecutive rows must instead sit flush against each other so
 * their hairline dividers read as one list.
 *
 * THIS is the prototype's `.settings` container, so this is where the 1040px cap
 * belongs: rows are the only thing the design bounds, keeping the label/control
 * relationship readable. Everything around them — the page header, the tab
 * strip, tables — is fluid to the right edge (`--content-width: 100%`), which is
 * what `SettingsPage` now renders. Do not move the cap back up a level.
 *
 * `max-w-[65rem]` is the ONE non-token size in this kit — the design system has
 * no container/measure token, and the prototype's container width is a layout
 * decision rather than a themable value. Everything else here is a DS token
 * utility. Keep whitespace after it in the template below: Tailwind's scan of
 * this directory is textual, and an arbitrary-value utility written flush
 * against a template interpolation is silently never generated. See
 * `tests/unit/Components/DS/KitUtilityExtraction.test.ts`.
 */
export const SettingsRowGroup = ({ gap = 'none', children, className }: SettingsRowGroupProps) => (
  <div
    data-lula-ds=""
    className={`flex max-w-[65rem] flex-col font-sans${GAPS[gap]}${className ? ` ${className}` : ''}`}
  >
    {children}
  </div>
);

export default SettingsRowGroup;
